import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { type BannerFact } from '@bms2/shared';
import { drawnMoney } from '../../components/Money';
import styles from './PageFacts.module.css';

/**
 * 🔒 A fact as the screen draws it (decision 0717): the server's `BannerFact`,
 * whose value is text because it crosses the wire, or a value the page composed
 * itself as a node, which is how a figure held in cents reaches the Money
 * component rather than a formatted string.
 */
export type ScreenFact = Omit<BannerFact, 'value'> & { value: ReactNode };
/** 🔒 A text value is scanned for a negative amount only where its fact says it is money (decision 2155); a node draws as it stands. */
const drawnFact = (fact: ScreenFact): ReactNode => (fact.money ? drawnMoney(fact.value) : fact.value);

/**
 * 🔒 A page's identifying facts, drawn in the page's own body (T170). The title
 * bar standard carries no facts row, so a page whose facts have no tile and no
 * sheet to read on draws them here, directly under the banner and above whatever
 * the page is about.
 *
 * 💰 They stood in the banner from decision 0335 until 2026-09-22, when the
 * product owner asked them out of it: *"do not put the details in the title bar
 * like Account or against invoice it is in the details or pdf view. This is a
 * break in the title bar standard and not allowed by the base standard."*
 *
 * 🔨 A fact with no value draws nothing. 🔒 A figure the page holds in cents is
 * handed over as the Money component, which draws it (decision 0717); a fact the
 * server already wrote as text is drawn through the component's text form, so a
 * negative amount reads red either way (decision 0481).
 */
export function PageFacts({ facts, label }: { facts: readonly ScreenFact[]; label?: string }) {
  const shown = facts.filter((fact) => fact.value !== null && fact.value !== undefined && fact.value !== '');
  if (shown.length === 0) return null;
  return (
    <dl className={styles.facts} data-page-facts={String(shown.length)} {...(label ? { 'aria-label': label } : {})}>
      {shown.map((fact) => (
        <div key={fact.label} className={styles.fact} data-page-fact={fact.label}>
          <dt className={styles.label}>{fact.label}</dt>
          <dd className={styles.value}>
            {fact.route ? <Link className={styles.link} to={fact.route}>{drawnFact(fact)}</Link> : drawnFact(fact)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

